import LinkifyImport from 'react-linkify';

import { observer } from 'mobx-react-lite';
import styled from 'styled-components';

import { Button } from '@components/button';
import { Card } from '@components/card';
import { FormLabel } from '@components/form-label';
import { IconAttachment } from '@components/icons/attachment';
import { IconCheckboxChecked } from '@components/icons/checkbox-checked';
import { IconOpen } from '@components/icons/open';
import { IconSelectMultiple } from '@components/icons/select-multiple';
import { IconSelectSingle } from '@components/icons/select-single';
import { IconTextLong } from '@components/icons/text-long';
import { IconTextShort } from '@components/icons/text-short';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { cjsDefault } from '@utils/cjs-default';

import { useModal } from 'features/modals';
import { useStore } from 'models/Provider';

const Linkify = cjsDefault(LinkifyImport);

const Answer = observer(({ q, application }) => {
  const { t } = useStore();
  const { showFullscreenModal } = useModal();
  const { value, type } = q;

  if (!value) {
    return (
      <Text
        font="regular14"
        color="purple60"
        style={{ fontStyle: 'italic' }}
        text={t('application.details.noanswer')}
      />
    );
  }

  switch (type) {
    case 'dropdown':
      if (q.isDeleted) {
        return <Text text={value} color="purple60" style={{ fontStyle: 'italic' }} />;
      }
      return (
        <Stack space={2} orientation="horizontal">
          <IconCheckboxChecked color="purple100" />
          {value ? (
            <Text text={value} />
          ) : (
            <Text text={t('application.details.deletedoption')} style={{ fontStyle: 'italic' }} />
          )}
        </Stack>
      );
    case 'multidropdown':
      if (q.isDeleted) {
        return <Text text={value} color="purple60" style={{ fontStyle: 'italic' }} />;
      }
      return (
        <Stack space={2}>
          {value.map((option, index) => (
            <Stack space={2} key={index} orientation="horizontal">
              <IconCheckboxChecked color="purple100" />
              <Text
                text={option ? option : t('application.details.deletedoption')}
                color={option ? undefined : 'purple60'}
                style={option ? undefined : { fontStyle: 'italic' }}
              />
            </Stack>
          ))}
        </Stack>
      );
    case 'attachment':
      const attachments = application.attachments.filter((a) => a.questionId !== null);
      const isArray = Array.isArray(value);
      if (!isArray && !application.attachMap[value]) {
        return (
          <Text
            font="regular14"
            color="purple60"
            style={{ fontStyle: 'italic' }}
            text={t('application.details.noanswer')}
          />
        );
      }
      if (isArray) {
        return (
          <Stack space={2} align="flex-start">
            {value.map((v, index) => (
              <Button
                key={index}
                tooltip={t('application.details.attachtip')}
                onClick={() => {
                  showFullscreenModal('FILE_PREVIEW', { application, value: v, attachments });
                }}
                variant="secondary"
                iconLeft={<IconOpen />}
                text={application.attachMap[v].fileName}
              />
            ))}
          </Stack>
        );
      } else {
        return (
          <Button
            tooltip={t('application.details.attachtip')}
            onClick={() => {
              showFullscreenModal('FILE_PREVIEW', { application, value, attachments });
            }}
            variant="secondary"
            iconLeft={<IconOpen />}
            text={application.attachMap[value].fileName}
          />
        );
      }
    case 'one_line':
    case 'multi_line':
    default:
      return (
        <Linkify properties={{ target: '_blank' }}>
          <Text font="regular16" text={value} children={value} />
        </Linkify>
      );
  }
});

export const QuestionAnswer = observer(({ q, application }) => {
  return (
    <Stack space={2}>
      <Stack space={2} orientation="horizontal">
        {iconForQuestionType(q.type)}
        <FormLabel text={q.title} />
      </Stack>
      <AnswerCard space={4}>
        <Answer q={q} application={application} />
      </AnswerCard>
    </Stack>
  );
});

export const AnswerCard = styled(Card)((p) => ({
  a: {
    cursor: 'pointer',
    opacity: 1,
    color: p.theme.colors.blue100,
    ':hover': {
      textDecoration: 'underline',
    },
  },
}));

export const iconForQuestionType = (type) => {
  switch (type) {
    case 'multi_line':
      return <IconTextLong color="elementPurple50" />;
    case 'dropdown':
      return <IconSelectSingle color="elementPurple50" />;
    case 'multidropdown':
      return <IconSelectMultiple color="elementPurple50" />;
    case 'attachment':
      return <IconAttachment color="elementPurple50" />;
    case 'one_line':
    default:
      return <IconTextShort color="elementPurple50" />;
  }
};
